/**
 * #577 lane A (L19) — the sample-week choice and its one-tap clear, for onboarding AND Settings.
 *
 * Spec: chorestory-next redesign/workflows/onboarding.md, the `SAMPLE{"Start with sample data?"}`
 * node ("keep sample week" / "start clean") and the Notes ("sample-data flag with one-tap clear
 * (also reachable later from settings)"). Owner decisions d-20260925-10 and d-20260925-11.
 *
 * ONE component, so both screens say the same true things from the same server answers:
 *
 * - The state comes from `GET /api/onboarding/samples`, never from the quest list. A failed read is
 *   "we couldn't check", never "no examples".
 * - Nothing is announced before the server answers. "Removed" and "added" are said only for a
 *   receipt `api/sampleWeek.js` has checked; a lost response, a 5xx or an untrusted 2xx says "we
 *   couldn't confirm" and offers "Check again", because the write may have landed.
 * - A refusal (4xx) says nothing changed, and why. A used example is explained, not retried in a loop.
 * - One request at a time: a synchronous ref holds a second tap in the same tick.
 * - Answers for a family this panel no longer shows, or after it unmounts, are dropped.
 *
 * `mode="onboarding"` offers the choice while no set exists and setup is unfinished. `mode="settings"`
 * shows nothing unless the family has examples, or a result to report.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from './Button';
import {
  classifySampleError,
  clearSampleWeek as clearOnServer,
  fetchSampleWeek,
  keepSampleWeek as keepOnServer,
} from '../api/sampleWeek.js';

export const CHECKING = 'Checking for example quests…';
export const CHECK_FAILED = "We couldn't check for example quests just now.";
export const KEEP_QUESTION = 'Start with a sample week?';
export const KEEP_EXPLAIN =
  "We can add two example quests — Make Bed and Wash Dishes — so the app isn't empty. They are real quests, marked Example, with no money attached. Clear them with one tap, here or later in Settings, until any example has recorded activity.";
export const KEEP_LABEL = 'Keep a sample week';
export const CLEAN_LABEL = 'Start clean';
export const CLEAN_CHOSEN = 'Starting clean — no example quests were added.';
export const KEEPING = 'Adding the sample week…';
export const ALREADY_KEPT = 'Your sample week is already here — nothing new was added.';
export const KEEP_UNKNOWN = "We couldn't confirm the sample week was added. Check again to see.";
export const KEEP_FINISHED = "Setup is finished, so a sample week can't be added any more.";
export const REMOVE_LABEL = 'Remove example quests';
export const REMOVING = 'Removing example quests…';
export const NONE_REMAIN = 'No example quests remain.';
export const CLEAR_UNKNOWN = "We couldn't confirm the examples were removed. Check again to see.";
export const RELOAD_FAILED = "We couldn't refresh the list — check again to see what is left.";
export const CHECK_AGAIN = 'Check again';
export const STALE_LIST = 'This list may be out of date — check again to see the latest.';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const listNames = (names) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

export const keptMessage = (n) => `${plural(n, 'example quest')} added. They are marked Example.`;
export const removedMessage = (n) => `${plural(n, 'example quest')} removed.`;
export const noteMessage = (samples) =>
  `Your family has ${plural(samples.length, 'example quest')}: ${listNames(samples.map((s) => s.name))}. You can remove them with one tap, including any you have edited, until any example has recorded activity. Your other quests stay.`;
export const historyMessage = (names) =>
  `${names.length ? listNames(names) : 'An example quest'} has recorded activity, so the examples cannot be removed. Completions are never erased.`;
export const clearRefusedHistory = (names) =>
  `An example quest has recorded activity${names.length ? ` (${listNames(names)})` : ''}, so nothing was removed.`;

/** One sentence for a refusal that is not about history or finished setup. Nothing changed in each. */
export function refusalMessage({ status, code }) {
  if (code === 'PARENT_REAUTH_REQUIRED') return "The parent check wasn't finished, so nothing changed.";
  if (status === 401 || code === 'NO_SESSION') return 'Your sign-in has expired — sign in again, then try once more. Nothing changed.';
  if (status === 403) return 'Only a parent can change the example quests. Nothing changed.';
  if (status === 429) return 'Too many tries — wait a minute, then try again. Nothing changed.';
  return "That didn't go through, so nothing changed.";
}

export default function SampleWeekPanel({ familyId, mode = 'onboarding', onChanged, onPendingChange, className, headingLevel = 3 }) {
  const [read, setRead] = useState({ status: 'loading', state: null });
  const [action, setAction] = useState(null); // null | 'keeping' | 'clearing'
  const [notice, setNotice] = useState(null); // { tone, message, checkAgain? }
  const [choseClean, setChoseClean] = useState(false);
  const inFlightRef = useRef(false);
  // Which family's answers may be published. Bumped on every family change and on unmount.
  const genRef = useRef(0);
  // Reads within one family also need ordering; mutations invalidate earlier reads.
  const readSeqRef = useRef(0);
  // Whether `onPendingChange(true)` is outstanding, and the latest callback. The host holds its
  // ceremony button on `true`, so a panel that goes away mid-request must still say `false`
  // (review M-2): the request's own `finally` is fenced off by the generation and cannot.
  const reportedPendingRef = useRef(false);
  const onPendingRef = useRef(onPendingChange);
  onPendingRef.current = onPendingChange;

  const load = useCallback(async (gen = genRef.current) => {
    const seq = ++readSeqRef.current;
    try {
      const state = await fetchSampleWeek(familyId);
      if (gen !== genRef.current || seq !== readSeqRef.current) return undefined;
      setRead({ status: 'ready', state });
      return state;
    } catch {
      if (gen !== genRef.current || seq !== readSeqRef.current) return undefined;
      setRead((prev) => (prev.state ? { ...prev, stale: true } : { status: 'failed', state: null }));
      return null;
    }
  }, [familyId]);

  useEffect(() => {
    if (!familyId) return undefined;
    const gen = ++genRef.current;
    setRead({ status: 'loading', state: null });
    setNotice(null);
    setChoseClean(false);
    // A request for the previous family may still be in flight; its busy state is not this one's.
    setAction(null);
    load(gen);
    return () => {
      genRef.current += 1;
      // The request in flight belongs to the family (or the panel) that is going away. Release its
      // hold, so neither the host's button nor this panel's next tap waits on an answer that will
      // be dropped.
      inFlightRef.current = false;
      if (reportedPendingRef.current) {
        reportedPendingRef.current = false;
        if (typeof onPendingRef.current === 'function') onPendingRef.current(false);
      }
    };
  }, [familyId, load]);

  const setPending = useCallback((next) => {
    setAction(next);
    reportedPendingRef.current = next !== null;
    if (typeof onPendingRef.current === 'function') onPendingRef.current(next !== null);
  }, []);

  const checkAgain = useCallback(async () => {
    const gen = genRef.current;
    const state = await load(gen);
    if (gen !== genRef.current || state === undefined) return;
    setNotice(state ? null : { tone: 'warn', message: CHECK_FAILED, checkAgain: true });
  }, [load]);

  const keep = useCallback(async () => {
    if (inFlightRef.current || !familyId) return;
    inFlightRef.current = true;
    readSeqRef.current += 1;
    const gen = genRef.current;
    setNotice(null);
    setPending('keeping');
    try {
      const kept = await keepOnServer(familyId);
      if (gen !== genRef.current) return;
      setRead({ status: 'ready', state: kept });
      setNotice({ tone: 'ok', message: kept.outcome === 'created' ? keptMessage(kept.samples.length) : ALREADY_KEPT });
      if (typeof onChanged === 'function') onChanged();
    } catch (error) {
      if (gen !== genRef.current) return;
      const c = classifySampleError(error);
      if (c.kind === 'unknown') {
        setRead((prev) => ({ ...prev, uncertain: true }));
        setNotice({ tone: 'warn', message: KEEP_UNKNOWN, checkAgain: true });
      } else if (c.code === 'ONBOARDING_COMPLETE') {
        setNotice({ tone: 'warn', message: KEEP_FINISHED });
        load(gen);
      } else {
        setNotice({ tone: 'warn', message: refusalMessage(c) });
      }
    } finally {
      // Only the current family's request releases the hold; the cleanup released a stale one.
      if (gen === genRef.current) {
        inFlightRef.current = false;
        setPending(null);
      }
    }
  }, [familyId, load, onChanged, setPending]);

  const clear = useCallback(async () => {
    if (inFlightRef.current || !familyId) return;
    inFlightRef.current = true;
    readSeqRef.current += 1;
    const gen = genRef.current;
    const before = read.state ? read.state.samples : [];
    setNotice(null);
    setPending('clearing');
    try {
      const receipt = await clearOnServer(familyId);
      if (gen !== genRef.current) return;
      const confirmed = receipt.outcome === 'removed' ? removedMessage(receipt.deletedIds.length) : NONE_REMAIN;
      // The removal is CONFIRMED now. Drop exactly the ids the server returned, then re-read.
      setRead((prev) => (prev.state
        ? { ...prev, state: { ...prev.state, samples: prev.state.samples.filter((s) => !receipt.deletedIds.includes(s.id)) } }
        : prev));
      if (typeof onChanged === 'function') onChanged();
      const fresh = await load(gen);
      if (gen !== genRef.current) return;
      setNotice(fresh
        ? { tone: 'ok', message: confirmed }
        : { tone: 'warn', message: `${confirmed} ${RELOAD_FAILED}`, checkAgain: true });
    } catch (error) {
      if (gen !== genRef.current) return;
      const c = classifySampleError(error);
      if (c.kind === 'unknown') {
        setNotice({ tone: 'warn', message: CLEAR_UNKNOWN, checkAgain: true });
      } else if (c.code === 'SAMPLES_HAVE_HISTORY') {
        const names = before.filter((s) => c.blockedIds.includes(s.id)).map((s) => s.name);
        setNotice({ tone: 'warn', message: clearRefusedHistory(names) });
        load(gen);
      } else {
        setNotice({ tone: 'warn', message: refusalMessage(c) });
      }
    } finally {
      // Only the current family's request releases the hold; the cleanup released a stale one.
      if (gen === genRef.current) {
        inFlightRef.current = false;
        setPending(null);
      }
    }
  }, [familyId, load, onChanged, read.state, setPending]);

  const Heading = `h${Math.min(Math.max(Number(headingLevel) || 3, 2), 6)}`;
  const samples = read.state ? read.state.samples : [];
  const used = samples.filter((s) => s.hasHistory).map((s) => s.name);
  const busy = action !== null;

  let body = null;
  if (read.status === 'loading') {
    body = mode === 'onboarding' ? <p className="swp__line">{CHECKING}</p> : null;
  } else if (read.status === 'failed') {
    body = (
      <>
        <p className="swp__line">{CHECK_FAILED}</p>
        <Button variant="quiet" size="sm" onClick={checkAgain}>{CHECK_AGAIN}</Button>
      </>
    );
  } else if (samples.length > 0) {
    body = (
      <>
        <p className="swp__line">{action === 'clearing' ? REMOVING : noteMessage(samples)}</p>
        {used.length > 0 ? (
          <p className="swp__line">{historyMessage(used)}</p>
        ) : (
          <Button variant="quiet" size="sm" onClick={clear} disabled={busy} loading={action === 'clearing'} busyLabel={REMOVING}>
            {REMOVE_LABEL}
          </Button>
        )}
      </>
    );
  } else if (mode === 'onboarding' && read.state.canKeep && !choseClean && !read.uncertain) {
    body = (
      <>
        <p className="swp__line">{action === 'keeping' ? KEEPING : KEEP_EXPLAIN}</p>
        <div className="swp__choice" role="group" aria-label={KEEP_QUESTION} style={{ display: 'flex', gap: 'var(--s-2, 8px)', flexWrap: 'wrap' }}>
          <Button variant="primary" size="sm" onClick={keep} disabled={busy} loading={action === 'keeping'} busyLabel={KEEPING}>
            {KEEP_LABEL}
          </Button>
          <Button variant="quiet" size="sm" onClick={() => { setChoseClean(true); setNotice(null); }} disabled={busy}>
            {CLEAN_LABEL}
          </Button>
        </div>
      </>
    );
  } else if (mode === 'onboarding' && choseClean) {
    body = <p className="swp__line">{CLEAN_CHOSEN}</p>;
  }

  const showCard = body !== null || notice !== null;
  if (!showCard) return null;

  return (
    <section
      className={['swp', className].filter(Boolean).join(' ')}
      data-component="SampleWeekPanel"
      data-mode={mode}
      aria-busy={busy ? 'true' : undefined}
      aria-label="Example quests"
      style={{ display: 'grid', gap: 'var(--s-2, 8px)' }}
    >
      <Heading className="swp__head" style={{ margin: 0 }}>
        {mode === 'onboarding' && samples.length === 0 && read.state?.canKeep ? KEEP_QUESTION : 'Example quests'}
      </Heading>
      {body}
      <p className="swp__notice" role="status" aria-live="polite" data-tone={notice?.tone || 'info'} style={{ margin: 0 }}>
        {notice?.message || ''}
      </p>
      {/* Review M-3: a later read failed and the last list was kept. Say so, unless the notice
          above already offers Check again for the same reason. */}
      {read.stale && !notice?.checkAgain ? <p className="swp__line">{STALE_LIST}</p> : null}
      {notice?.checkAgain || read.stale ? (
        <Button variant="quiet" size="sm" onClick={checkAgain} disabled={busy}>{CHECK_AGAIN}</Button>
      ) : null}
    </section>
  );
}
